"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMVariableResolver = void 0;
const inversify_1 = require("inversify");
const VMSharedTypes_1 = require("../../types/vm-transformers/VMSharedTypes");
const VMConstants_1 = require("../VMConstants");
let VMVariableResolver = class VMVariableResolver {
    resolve(varName, context, isStore = false) {
        var _a;
        let { localIndex: foundLocalIndex, scopedKey: foundScopedKey, depth: foundAtDepth } = this.findBlockScopedVariable(varName, context);
        if (foundLocalIndex !== undefined && foundAtDepth !== undefined && foundAtDepth > 0) {
            if (this.checkTDZShadowsLocal(varName, foundAtDepth, context)) {
                return {
                    location: VMSharedTypes_1.VariableLocation.TDZ,
                    index: 0,
                    name: varName
                };
            }
            const { isEnv: isCurrentContextEnv, isPerIterationVar: isPerIterationLoopVar } = this.checkBlockScopedEnvStatus(varName, foundScopedKey, context);
            if (isCurrentContextEnv) {
                const shadowsInSameEnv = this.checkShadowsInSameEnv(varName, foundAtDepth, foundScopedKey, context);
                const usesScopedKey = !isPerIterationLoopVar && (foundAtDepth > 1 || shadowsInSameEnv);
                const envKey = usesScopedKey && foundScopedKey ? foundScopedKey : varName;
                const index = context.constantPool.add(envKey);
                return {
                    location: VMSharedTypes_1.VariableLocation.Environment,
                    index,
                    name: varName
                };
            }
            return {
                location: VMSharedTypes_1.VariableLocation.Local,
                index: foundLocalIndex,
                scopedKey: foundScopedKey,
                name: varName
            };
        }
        const paramIndex = context.parameters.get(varName);
        if (paramIndex !== undefined && context.parametersInTDZ.has(varName)) {
            return {
                location: VMSharedTypes_1.VariableLocation.TDZ,
                index: paramIndex,
                name: varName
            };
        }
        if (paramIndex !== undefined && !context.envVars.has(varName)) {
            return {
                location: VMSharedTypes_1.VariableLocation.Parameter,
                index: paramIndex,
                name: varName
            };
        }
        if (foundLocalIndex === undefined) {
            foundLocalIndex = context.localVariables.get(varName);
        }
        if (foundLocalIndex !== undefined) {
            if (this.shouldSkipLocalForEnv(varName, foundScopedKey, context)) {
                foundLocalIndex = undefined;
            }
            else {
                if (this.checkTDZShadowsLocal(varName, foundAtDepth, context) ||
                    this.checkPlainLocalShadowedByTDZ(varName, foundAtDepth, context)) {
                    return {
                        location: VMSharedTypes_1.VariableLocation.TDZ,
                        index: 0,
                        name: varName
                    };
                }
                return {
                    location: VMSharedTypes_1.VariableLocation.Local,
                    index: foundLocalIndex,
                    name: varName
                };
            }
        }
        if (this.checkUndeclaredInTDZ(varName, context)) {
            return {
                location: VMSharedTypes_1.VariableLocation.TDZ,
                index: 0,
                name: varName
            };
        }
        if (this.isEnvVariable(varName, context)) {
            const envKey = this.resolveEnvKeyFromParentContexts(varName, context);
            const index = context.constantPool.add(envKey);
            return {
                location: VMSharedTypes_1.VariableLocation.Environment,
                index,
                name: varName
            };
        }
        const isTopLevel = this.isTopLevelVariable(varName, context);
        if (isTopLevel && !context.parentContext) {
            const index = context.constantPool.add(varName);
            return {
                location: VMSharedTypes_1.VariableLocation.Global,
                index,
                name: varName
            };
        }
        const parentScopeResult = this.isVariableInParentScope(varName, context);
        if (parentScopeResult.found && parentScopeResult.inTDZ) {
            const index = context.constantPool.add(varName);
            return {
                location: VMSharedTypes_1.VariableLocation.TDZ,
                index,
                name: varName
            };
        }
        if (parentScopeResult.found && parentScopeResult.context) {
            const targetContext = parentScopeResult.context;
            const isParentParameter = targetContext.parameters.has(varName) && !targetContext.parametersInTDZ.has(varName);
            if ((_a = (isStore || parentScopeResult.isForwardRef)) !== null && _a !== void 0 ? _a : (targetContext.envVars.has(varName) || targetContext.capturedVars.has(varName))) {
                targetContext.capturedVars.add(varName);
                if (!isParentParameter) {
                    targetContext.envVars.add(varName);
                }
            }
            if (isTopLevel) {
                const index = context.constantPool.add(varName);
                return {
                    location: VMSharedTypes_1.VariableLocation.Global,
                    index,
                    name: varName
                };
            }
            const index = context.constantPool.add(varName);
            return {
                location: VMSharedTypes_1.VariableLocation.Environment,
                index,
                name: varName
            };
        }
        const index = context.constantPool.add(varName);
        return {
            location: VMSharedTypes_1.VariableLocation.Global,
            index,
            name: varName
        };
    }
    isEnvVariable(varName, context) {
        let currentContext = context;
        while (currentContext) {
            if (currentContext.envVars.has(varName)) {
                return true;
            }
            currentContext = currentContext.parentContext;
        }
        return false;
    }
    findBlockScopedVariable(varName, context) {
        for (let depth = context.blockScopeDepth; depth >= 0; depth--) {
            const scopedKey = (0, VMConstants_1.getScopedVariableName)(varName, depth);
            if (context.activeBlockScopes && !context.activeBlockScopes.has(scopedKey)) {
                continue;
            }
            const localIndex = context.localVariables.get(scopedKey);
            if (localIndex !== undefined) {
                return { localIndex, scopedKey, depth };
            }
        }
        return {};
    }
    checkBlockScopedEnvStatus(varName, scopedKey, context) {
        var _a, _b, _c, _d;
        const scopedKeyIsInEnv = scopedKey !== undefined && context.envVars.has(scopedKey);
        const plainNameIsInEnv = context.envVars.has(varName);
        const isPerIterationLoopVarByName = (_b = (_a = context.perIterationLoopVars) === null || _a === void 0 ? void 0 : _a.has(varName)) !== null && _b !== void 0 ? _b : false;
        const isPerIterationVarScopedKey = scopedKey !== undefined && ((_d = (_c = context.perIterationVarScopedKeys) === null || _c === void 0 ? void 0 : _c.has(scopedKey)) !== null && _d !== void 0 ? _d : false);
        const isShadowingPerIterationVar = isPerIterationLoopVarByName && scopedKey !== undefined && !isPerIterationVarScopedKey;
        const isEnv = scopedKeyIsInEnv ||
            (plainNameIsInEnv && !isShadowingPerIterationVar) ||
            (isPerIterationLoopVarByName && !isShadowingPerIterationVar);
        const isPerIterationVar = isPerIterationLoopVarByName && !isShadowingPerIterationVar;
        return { isEnv, isPerIterationVar };
    }
    checkShadowsInSameEnv(varName, foundAtDepth, foundScopedKey, context) {
        if (foundAtDepth !== 1 || context.envDepth !== 1 || !foundScopedKey) {
            return false;
        }
        const depth0Key = (0, VMConstants_1.getScopedVariableName)(varName, 0);
        const hasAtDepth0 = context.variableScopes.has(depth0Key) || context.variableScopes.has(varName);
        return hasAtDepth0 && context.envVars.has(varName);
    }
    shouldSkipLocalForEnv(varName, foundScopedKey, context) {
        var _a, _b, _c, _d;
        if (context.hoistedFunctionDeclarations.has(varName)) {
            return false;
        }
        const isDestructuredParam = ((_b = (_a = context.destructuredParameterNames) === null || _a === void 0 ? void 0 : _a.has(varName)) !== null && _b !== void 0 ? _b : false) ||
            (foundScopedKey !== undefined && ((_d = (_c = context.destructuredParameterNames) === null || _c === void 0 ? void 0 : _c.has(foundScopedKey)) !== null && _d !== void 0 ? _d : false));
        if (isDestructuredParam) {
            return false;
        }
        if (varName === context.functionName) {
            return false;
        }
        return context.envVars.has(varName);
    }
    checkTDZShadowsLocal(varName, foundAtDepth, context) {
        if (foundAtDepth === undefined || foundAtDepth >= context.blockScopeDepth) {
            return false;
        }
        if (!context.localVariablesInTDZ.has(varName)) {
            return false;
        }
        return !this.isPerIterationBypassingTDZ(varName, context);
    }
    checkPlainLocalShadowedByTDZ(varName, foundAtDepth, context) {
        if (foundAtDepth !== undefined) {
            return false;
        }
        if (!context.localVariablesInTDZ.has(varName)) {
            return false;
        }
        return !this.isPerIterationBypassingTDZ(varName, context);
    }
    isPerIterationBypassingTDZ(varName, context) {
        var _a, _b;
        return context.envVars.has(varName) && ((_b = (_a = context.perIterationLoopVars) === null || _a === void 0 ? void 0 : _a.has(varName)) !== null && _b !== void 0 ? _b : false);
    }
    checkUndeclaredInTDZ(varName, context) {
        if (!context.localVariablesInTDZ.has(varName)) {
            return false;
        }
        return !this.isPerIterationBypassingTDZ(varName, context);
    }
    resolveEnvKeyFromParentContexts(varName, context) {
        var _a, _b;
        const envKey = varName;
        let currentContext = context.parentContext;
        while (currentContext) {
            if (currentContext.localVariables.has(varName)) {
                return varName;
            }
            const maxDepth = Math.max(currentContext.blockScopeDepth, 10);
            let scopedVersionsCount = 0;
            for (let d = maxDepth; d >= 1; d--) {
                if (currentContext.localVariables.has((0, VMConstants_1.getScopedVariableName)(varName, d))) {
                    scopedVersionsCount++;
                }
            }
            const hasMultipleScopedVersions = scopedVersionsCount > 1;
            for (let depth = maxDepth; depth >= 2; depth--) {
                const scopedKey = (0, VMConstants_1.getScopedVariableName)(varName, depth);
                if (hasMultipleScopedVersions &&
                    depth > currentContext.blockScopeDepth &&
                    currentContext.activeBlockScopes &&
                    !currentContext.activeBlockScopes.has(scopedKey)) {
                    continue;
                }
                if (currentContext.localVariables.has(scopedKey)) {
                    const isPerIterationLoopVar = (_b = (_a = currentContext.perIterationLoopVars) === null || _a === void 0 ? void 0 : _a.has(varName)) !== null && _b !== void 0 ? _b : false;
                    const usesPlainName = isPerIterationLoopVar || depth <= 1;
                    return usesPlainName ? varName : scopedKey;
                }
            }
            currentContext = currentContext.parentContext;
        }
        return envKey;
    }
    isTopLevelVariable(varName, context) {
        var _a;
        let currentContext = context;
        while (currentContext) {
            if ((_a = currentContext.topLevelVariables) === null || _a === void 0 ? void 0 : _a.has(varName)) {
                return true;
            }
            currentContext = currentContext.parentContext;
        }
        return false;
    }
    isVariableInParentScope(varName, context) {
        var _a;
        let currentContext = context.parentContext;
        while (currentContext) {
            if (currentContext.functionName && varName === currentContext.functionName) {
                return { found: true, context: currentContext, isForwardRef: false };
            }
            if (currentContext.parameters.has(varName)) {
                const isInTDZ = currentContext.parametersInTDZ.has(varName);
                if (isInTDZ) {
                    return { found: true, context: currentContext, isForwardRef: false, inTDZ: true };
                }
                return { found: true, context: currentContext, isForwardRef: false };
            }
            if (currentContext.localVariables.has(varName)) {
                return { found: true, context: currentContext, isForwardRef: false };
            }
            for (let depth = currentContext.blockScopeDepth; depth >= 0; depth--) {
                const scopedKey = (0, VMConstants_1.getScopedVariableName)(varName, depth);
                if (currentContext.localVariables.has(scopedKey)) {
                    return { found: true, context: currentContext, isForwardRef: false };
                }
            }
            if ((_a = currentContext.declaredVariables) === null || _a === void 0 ? void 0 : _a.has(varName)) {
                const isInTDZ = currentContext.parametersInTDZ.has(varName);
                if (!isInTDZ) {
                    return { found: true, context: currentContext, isForwardRef: true };
                }
            }
            currentContext = currentContext.parentContext;
        }
        return { found: false };
    }
};
exports.VMVariableResolver = VMVariableResolver;
exports.VMVariableResolver = VMVariableResolver = __decorate([
    (0, inversify_1.injectable)()
], VMVariableResolver);
